import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { CONTACT } from "../data/site";

const LINKS = [
  { label: "Work", hash: "#work", testid: "nav-link-work" },
  { label: "Formats", hash: "#formats", testid: "nav-link-formats" },
  { label: "Process", hash: "#process", testid: "nav-link-process" },
  { label: "FAQ", hash: "#faq", testid: "nav-link-faq" },
  { label: "Free edit", href: CONTACT.freeEditLink, testid: "nav-link-free-edit" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (hash) => {
    setOpen(false);
    const scroll = () => window.__lenis?.scrollTo(hash, { offset: -70 });
    if (pathname !== "/") {
      navigate("/");
      setTimeout(scroll, 400);
    } else scroll();
  };

  return (
    <header
      data-testid="nav"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled || open
          ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-16 md:h-[72px]">
        <Link to="/" aria-label="VDYO home" data-testid="nav-logo">
          <Logo className="text-[22px] md:text-2xl" testid="nav-logo-mark" />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) =>
            l.href ? (
              <a
                key={l.testid}
                data-testid={l.testid}
                href={l.href}
                target="_blank"
                rel="noreferrer"
                className="font-heading text-sm font-semibold text-[#C8F031]/80 hover:text-[#C8F031] transition-colors min-h-[44px] inline-flex items-center"
              >
                {l.label}
              </a>
            ) : (
              <button
                key={l.hash}
                data-testid={l.testid}
                onClick={() => go(l.hash)}
                className="font-heading text-sm font-semibold text-white/60 hover:text-white transition-colors min-h-[44px]"
              >
                {l.label}
              </button>
            )
          )}
        </nav>

        <div className="flex items-center gap-2">
          <a
            data-testid="nav-free-edit-btn"
            href={CONTACT.freeEditLink}
            target="_blank"
            rel="noreferrer"
            className="btn-primary !px-5 md:!px-7"
          >
            Free 30-second edit
          </a>
          <button
            data-testid="nav-menu-btn"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            className="md:hidden w-11 h-11 flex items-center justify-center text-white"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="md:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl">
          <div className="container-x py-4 flex flex-col">
            {LINKS.map((l) =>
              l.href ? (
                <a
                  key={l.testid}
                  data-testid={`${l.testid}-mobile`}
                  href={l.href}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setOpen(false)}
                  className="text-left font-heading text-lg font-bold text-[#C8F031] py-3.5 border-b border-white/5 last:border-0 min-h-[44px]"
                >
                  {l.label}
                </a>
              ) : (
                <button
                  key={l.hash}
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => go(l.hash)}
                  className="text-left font-heading text-lg font-bold text-white/80 py-3.5 border-b border-white/5 last:border-0 min-h-[44px]"
                >
                  {l.label}
                </button>
              )
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Nav;
